import { CaretRightOutlined, CaretUpOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import { Space, Tooltip } from "antd";
import React, { useEffect } from "react";
import { ICustomer } from "../../../../api/Customer_V2/customerApi_V2";
import { NO_DATA } from "../../../../env";
import { FlattenedField } from "../../../CustomersV2/indexV2";
import { Column, TableProps, TableRow } from "./hook/types";

import './styles.scss';
export type WithRowClass<T> = {
    rowClassName?: (row: TableRow<T>, index: number) => string;
    onRowClick?: (row: T, rowIndex: number) => void;
    rowSelectionMode?: 'checkbox' | 'click' | 'both'; // 'checkbox': chỉ checkbox, 'click': chỉ click, 'both': cả hai
};

import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { selectorPermission } from "../../../../store/slices/permissionSlice";
import { capitalizeEachWord, getUserPermissions } from "../../../../until";
import './styles.scss';
import { upperCase } from "lodash";
import { NO_VIEW } from "../../../../services/data";

export type DataTableProps<T> = TableProps<T> & WithRowClass<T>;

export function reorder<T>(arr: T[], from: number, to: number) {
    const copy = arr.slice();
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);
    return copy;
}

export function useColumnResize(ref: React.RefObject<HTMLTableElement>, columns: Column<any>[]) {
    const [widths, setWidths] = React.useState<Record<string, number>>(
        Object.fromEntries(columns.map(c => [c.id, c.width ?? 160]))
    );

    const measureMinByHeader = React.useCallback((cid: string) => {
        const table = ref.current;
        if (!table) return 48;
        const th = table.querySelector<HTMLTableCellElement>(`th[data-cid="${cid}"]`);
        const btn = th?.querySelector<HTMLElement>('.dt-th-btn');
        if (!th || !btn) return 48;

        const prevWidth = btn.style.width;
        const prevMaxWidth = (btn.style as any).maxWidth || '';

        btn.style.width = 'max-content';
        (btn.style as any).maxWidth = 'col-resize';

        const intrinsic = Math.ceil(btn.getBoundingClientRect().width);

        btn.style.width = prevWidth;
        (btn.style as any).maxWidth = prevMaxWidth;

        return intrinsic + 8;
    }, [ref]);

    const onMouseDown = (cid: string, e: React.MouseEvent) => {
        e.preventDefault();
        const startX = e.clientX;
        const startWidth = widths[cid] ?? 160;

        const col = columns.find(c => c.id === cid);
        const min = (col?.minWidth) ?? 30;   // 👈 Cho phép nhỏ hơn
        const max = (col?.maxWidth) ?? 600;

        const onMove = (ev: MouseEvent) => {
            const dx = ev.clientX - startX;
            const next = Math.min(max, Math.max(min, startWidth + dx));
            setWidths(prev => ({ ...prev, [cid]: next }));
            // console.log(next)
        };
        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const onDoubleClick = (cid: string) => {
        const col = columns.find(c => c.id === cid);
        const confMin = (col?.minWidth) ?? 0;
        const w = Math.max(confMin, measureMinByHeader(cid));
        setWidths(prev => ({ ...prev, [cid]: w }));
    };

    useEffect(() => {
        setWidths(w => {
            const next = { ...w };
            columns.forEach(c => {
                if (next[c.id] == null) next[c.id] = c.width ?? 160;
            });
            return next;
        });
    }, [columns]);

    return { widths, onMouseDown, onDoubleClick };
}

//////////


export const numeric = new Set(["year", "price"]);
// ------------------------- Types for table rows -------------------------
type Role = "Admin" | "User" | "Guest";
export type RowKind = "group-city" | "group-role" | "detail";

// Bảng Car: detail là map fields
export type AppRowCar = {
    idTable?: number;
    idCover?: number | string;
    customerCodeDms?: string
    dmsId?: string
    idVehicle?: number;
    id: string;
    kind: RowKind;
    city?: string; // dùng cho group cấp 1 = carType
    role?: string; // dùng cho group cấp 2 = brand
    count?: number;
    fields?: Record<string, string | number | null | undefined>;
    customerId?: number
    ownerId?: number
    contactId?: number
    categoryCarId?: number
};

// ------------------------- Expand keys helpers -------------------------
export const kCity = (carType: string) => `city:${carType}`;
export const kRole = (carType: string, brand: string) => `role:${carType}|${brand}`;

// ------------------------- Build rows from dataCarTest -------------------------
export function getFieldValue(
    car: ICustomer,
    code: string
): string | number | null | undefined {
    return car.fields.find((f) => f.fieldCode === code)?.value as
        | string
        | number
        | null
        | undefined;
}

export function buildViewRowsCar(
    cars: ICustomer[],
    expanded: Set<string>
): TableRow<AppRowCar>[] {


    const out: TableRow<AppRowCar>[] = [];
    let autoId = 1;

    // Group theo own_vehicle_type_category -> brand
    const byType = new Map<string, ICustomer[]>();
    for (const c of cars) {
        const type = (getFieldValue(c, "own_vehicle_type_category") as string) || NO_DATA;
        if (!byType.has(type)) byType.set(type, []);
        byType.get(type)!.push(c);
    }

    // duyệt từng own_vehicle_type_category
    for (const [own_vehicle_type_category, carsOfType] of byType.entries()) {
        // push group own_vehicle_type_category
        out.push({
            id: `grp-city:${own_vehicle_type_category}`,
            data: {
                id: `grp-city:${own_vehicle_type_category}`,
                kind: "group-city",
                city: own_vehicle_type_category,
                count: carsOfType.length
            },
        });

        if (!expanded.has(kCity(own_vehicle_type_category))) continue;

        // group tiếp theo own_vehicle_brand
        const byBrand = new Map<string, ICustomer[]>();
        for (const c of carsOfType) {
            const own_vehicle_brand = (getFieldValue(c, "own_vehicle_brand") as string) || NO_DATA;
            if (!byBrand.has(own_vehicle_brand)) byBrand.set(own_vehicle_brand, []);
            byBrand.get(own_vehicle_brand)!.push(c);
        }

        for (const [own_vehicle_brand, brandCars] of byBrand.entries()) {
            out.push({
                id: `grp-role:${own_vehicle_type_category}:${own_vehicle_brand}`,
                data: {
                    id: `grp-role:${own_vehicle_type_category}:${own_vehicle_brand}`,
                    kind: "group-role",
                    city: own_vehicle_type_category,
                    role: own_vehicle_brand,
                    count: brandCars.length,
                },
            });

            if (!expanded.has(kRole(own_vehicle_type_category, own_vehicle_brand))) continue;

            for (const car of brandCars) {

                const fields: Record<string, any> = {};
                car.fields.forEach((f) => {
                    fields[f.fieldCode] = f.view === false ? NO_VIEW : (f.value ?? null);
                    if (f.additionalData) {
                        fields[`${f.fieldCode}__meta`] = f.additionalData;
                    }
                });
                const rowId = car.idTable + "-" + autoId++;

                out.push({
                    id: `${rowId}`,
                    data: {
                        // id: car.id.toString(),
                        customerCodeDms: car.customerCodeDms,
                        dmsId: car.dmsId,
                        idCover: car.id,
                        idVehicle: car.vehicleId,
                        id: rowId.toString(),
                        idTable: car.idTable,
                        kind: "detail",
                        city: own_vehicle_type_category,
                        role: own_vehicle_brand,
                        fields,
                        customerId: car?.customerId,
                        contactId: car?.contactId,
                        ownerId: car?.ownerId,
                        categoryCarId: car?.categoryCarId

                    },
                });
            }
        }
    }

    return out;
}

// ------------------------- Columns (static + dynamic) -------------------------
export function makeColumnsCar(
    expanded: Set<string>,
    isView: boolean,
    isEdit: boolean,
    toggleCity: (carType: string) => void,
    toggleRole: (carType: string, brand: string) => void,
    dataFilterActive: FlattenedField[],
    handleOpenModal: (type: string, id: number) => void,
    customerId?: number,
    linkedFields?: string[],  // ✅ danh sách tên field có gắn link
    permittedCategoryCarIds?: number[]
): Column<AppRowCar>[] {
    // Cột tĩnh: expand + Tên xe/Số lượng

    const staticCols: Column<AppRowCar>[] = [
        {
            id: "action",
            header: "Hành động",
            renderCell: ({ row }) => {
                const { id, idTable, kind } = row

                if (kind === "group-city") {
                    const isOpen = expanded.has(kCity(row.city!));
                    return (
                        <button
                            onClick={() => toggleCity(row.city!)}
                            style={{
                                all: "unset",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                fontWeight: 600,
                            }}
                        // title={row.city}
                        >
                            {!isOpen ? (
                                <CaretRightOutlined style={{ color: "#344054" }} />
                            ) : (
                                <CaretUpOutlined style={{ color: "#40A9FF" }} />
                            )}

                            <Tooltip
                                placement="right"
                                color="white"
                                title={
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <span className="color-bage-custom fs-14">
                                                {row.city}
                                            </span>
                                        </div>
                                    </div>
                                }
                            >
                                <span>{row.city}</span>
                            </Tooltip>
                        </button>
                    );
                }
                if (kind === "group-role") {
                    const isOpen = expanded.has(kRole(row.city!, row.role!));
                    return (
                        <div style={{ paddingLeft: 28 }}>
                            <button
                                onClick={() => toggleRole(row.city!, row.role!)}
                                style={{
                                    all: "unset",
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 8,
                                    fontWeight: 600,
                                    border: 'none',
                                    borderRadius: 8
                                }}
                            // title={row.role}
                            >
                                {!isOpen ? (
                                    <CaretRightOutlined style={{ color: "#344054" }} />
                                ) : (
                                    <CaretUpOutlined style={{ color: "#40A9FF" }} />
                                )}

                                <Tooltip
                                    placement="right"
                                    color="white"
                                    title={
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2">
                                                <span className="color-bage-custom fs-14">
                                                    {row.role}
                                                </span>
                                            </div>
                                        </div>
                                    }
                                >
                                    <span>{row.role}</span>
                                </Tooltip>
                            </button>
                        </div>
                    );
                }
                return (
                    <Space size="middle" className="w-full justify-center items-center">

                        {
                            permittedCategoryCarIds?.includes(row?.categoryCarId ?? -1)
                                ?
                                <div className="custom-page-css w-full flex justify-center items-center gap-3">
                                    {
                                        isView &&
                                        <EyeOutlined className="custom-page-css text-sm border-D9D9D9 p-5-px bg-white rounded "
                                            onClick={() => handleOpenModal('view', Number(idTable))}
                                        />
                                    }

                                    {
                                        isEdit && row.fields?.own_vehicle_status !== 'Đã bán' &&
                                        <EditOutlined className="custom-page-css text-sm border-D9D9D9 p-5-px bg-white rounded"
                                            onClick={() => handleOpenModal('update', Number(idTable))}
                                        />
                                    }
                                </div>
                                :
                                ''
                        }
                    </Space>

                );
            },
        },
        {
            id: "own_vehicle_name",
            header: "Tên xe",
            sortable: true,
            renderCell: ({ row }) => {
                if (row.kind !== "detail") {
                    return (
                        <span className="text-096DD9">
                            Số lượng: {row.count}
                        </span>
                    );
                }
                return (
                    <span>
                        {row.fields?.["own_vehicle_name"] as string}
                    </span>
                );
            },
        },
    ];

    // Cột động: theo cấu hình + columnsMapShow, bỏ nameCar để tránh trùng với cột tĩnh
    const dynamicFields = dataFilterActive.filter((f) => f.fieldCode !== "own_vehicle_name");// loại bỏ trùng tên xe

    const STATUS_FIELDS = ["interaction_vehicle_status", "interaction_group_status_name", "interaction_customer_last_assessment", "status", "customer_assessment", "customer_last_assessment", "own_vehicle_status"];

    const dynamicCols: Column<AppRowCar>[] = dynamicFields.map((f) => ({
        id: f.fieldCode,
        header: f.fieldLabel,
        sortable: true,
        accessor: (row) => {
            if (row.kind !== "detail") return numeric.has(f.fieldCode) ? Number.NEGATIVE_INFINITY : "";
            const v = row.fields?.[f.fieldCode];
            return numeric.has(f.fieldCode) ? Number(v ?? 0) : String(v ?? "");
        },
        renderCell: ({ row }) =>
            row.kind === "detail" ?
                <span>
                    {(() => {
                        // if (f.fieldCode === "own_representative")
                        //     console.log(row.customerId, customerId, f.fieldCode,row.fields?.[f.fieldCode]);

                        if (STATUS_FIELDS.includes(f.fieldCode)) {

                            const colors = (row.fields?.[`${f.fieldCode}__meta`] as any)?.colors;
                            return (
                                <span
                                    className="custom-page-css flex items-center justify-center rounded w-fit h-8 p-2"
                                    style={{
                                        border: `1px solid ${colors?.border}`,
                                        color: `${colors?.text}`,
                                        background: `${colors?.bg}`,
                                    }}
                                >
                                    {row.fields?.[f.fieldCode] || NO_DATA}
                                </span>
                            );


                        }
                        switch (f?.fieldCode) {
                            case "own_chassis_no":
                            case "own_number_plate":
                                return <span>{String(row.fields?.[f.fieldCode] ?? "")?.toUpperCase()}</span>;
                            // case "own_representative":
                            //     return <span>{row.fields?.[f.fieldCode] ? capitalizeEachWord(String(row.fields?.[f.fieldCode])) : NO_DATA}</span>;
                            case "customer_name": //chủ sở hữu dùng ownerId
                            case "own_representative": //người đại diện và người liên hệ diện contactId 
                                {
                                    const tagetId = f.fieldCode === "own_representative" ? row?.contactId : row?.ownerId
                                    //  console.log(tagetId,row?.contactId , row?.ownerId );
                                    if ((tagetId !== customerId) && customerId && tagetId) {
                                        const showLink = linkedFields?.includes(f.fieldCode)
                                        if (showLink) {
                                            return (
                                                <Link
                                                    style={{
                                                        color: "#1677ff",
                                                        cursor: "pointer",
                                                        fontWeight: 500,
                                                        textTransform: 'capitalize'
                                                    }}
                                                    to="/customer/list/edit"
                                                    state={{
                                                        id: tagetId,
                                                    }}
                                                >
                                                    {row.fields?.[f.fieldCode] ? capitalizeEachWord(String(row.fields[f.fieldCode])) : NO_DATA}
                                                </Link>
                                            )
                                        } else {
                                            return (
                                                <span>{row.fields?.[f.fieldCode] ? capitalizeEachWord(String(row.fields[f.fieldCode])) : NO_DATA}</span>
                                            )
                                        }

                                    }
                                    else
                                        return (
                                            <span>{row.fields?.[f.fieldCode] ? capitalizeEachWord(String(row.fields[f.fieldCode])) : NO_DATA}</span>
                                        )
                                }
                            default:
                                return <span>{row.fields?.[f.fieldCode] ?? NO_DATA}</span>;
                        }
                    })()}
                </span> :
                null,
    }));

    return [...staticCols, ...dynamicCols];
}